import { useCallback, useMemo, useState } from 'react';

import { ICreatorReplyProps } from '~/components/discussions/card/CreatorReply';
import { useUserStatus } from '~/hooks/userStatus';
import {
  CommentV2,
  DISCUSSION_STATUS_APPROVED,
  DISCUSSION_STATUS_REJECTED,
} from '~/lib/discussions/types';
import { useCommentV2 } from '~/lib/discussions/useCommentV2';
import useUser from '~/lib/user';
import { CommentV2Type } from '~/static/discussions/enums';

function useCommentCard(
  comment: CommentV2,
  type: CommentV2Type,
  partnersIds: Array<number>,
) {
  const [stateHelpfulCount, setStateHelpfulCount] = useState(comment.up_votes);
  const [stateHasUserUpvoted, setStateHasUserUpvoted] = useState(
    comment.user_vote === 1,
  );

  const { like, dislike, disableVoteBtn } = useCommentV2(comment);
  const { user } = useUser();
  const { userStatus } = useUserStatus(comment.deal_id);

  const onUpvote = useCallback(() => {
    setStateHelpfulCount(stateHelpfulCount + 1);
    setStateHasUserUpvoted(true);

    like();
  }, [like, stateHelpfulCount]);

  const onDownvote = useCallback(() => {
    setStateHelpfulCount(stateHelpfulCount - 1);
    setStateHasUserUpvoted(false);

    dislike();
  }, [dislike, stateHelpfulCount]);

  const toggleVote = useCallback(() => {
    if (stateHasUserUpvoted) {
      onDownvote();
    } else {
      onUpvote();
    }
  }, [onDownvote, onUpvote, stateHasUserUpvoted]);

  const creatorReply: ICreatorReplyProps | null = useMemo(() => {
    let creatorComment: CommentV2 | undefined = undefined;
    const status = comment.status?.toLowerCase();
    const partnersIdsArray = comment.children?.filter((child: CommentV2) =>
      partnersIds.includes(child.user.id),
    );
    const notPartnersIdsArray = comment.children?.filter(
      (child) => !partnersIds.includes(child.user.id),
    );

    // If the status is approved and the array of partners ids is not empty
    // then the creator comment is the first element of the array of partners ids
    if (status === DISCUSSION_STATUS_APPROVED && partnersIdsArray?.length) {
      creatorComment = partnersIdsArray?.[0];
    }

    // If the status is rejected and the array of partners ids is empty
    // then the creator comment is the first element of the array of not partners ids
    if (status === DISCUSSION_STATUS_REJECTED && notPartnersIdsArray?.length) {
      creatorComment = notPartnersIdsArray?.[0];
    }

    // - If Approved visible for everybody
    // - If Declined visible only for staff, moderators and owner (not other sumolings)
    const isVisible =
      status === DISCUSSION_STATUS_APPROVED ||
      (status === DISCUSSION_STATUS_REJECTED &&
        (userStatus?.user_moderator || user?.id === comment.user.id));

    if (!isVisible || !creatorComment) {
      return null;
    }

    return {
      avatarUrl: creatorComment.user.avatar,
      creatorUsername: creatorComment.user.username,
      replyDate: creatorComment.created,
      replyText: creatorComment.comment,
      status: comment.status?.toLowerCase(),
      deleteReplyUrl: `/api/v2/deals/${comment.deal_id}/${type}s/${creatorComment.id}/`,
      allowOptions: user?.id === creatorComment.user.id,
    };
  }, [
    comment.status,
    comment.children,
    comment.user.id,
    comment.deal_id,
    userStatus?.user_moderator,
    user?.id,
    type,
    partnersIds,
  ]);

  return {
    stateHelpfulCount,
    stateHasUserUpvoted,
    toggleVote,
    disableVoteBtn,
    creatorReply,
  };
}

export default useCommentCard;
